import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { MapPin, Star, Calendar, Users, Check, FileText, Download, Mail, Printer, Home } from 'lucide-react';
import hotelData from '../../data/hotels.json';

const HotelConfirmation = () => {
  const { bookingId } = useParams();
  const location = useLocation();
//   const navigate = useNavigate();
  
  // Get booking data from route state if available
  const [hotel, setHotel] = useState(location.state?.hotel || null);
  const [room, setRoom] = useState(location.state?.room || null);
  const [booking, setBooking] = useState(location.state?.booking || null);
  
  const [loading, setLoading] = useState(!booking);
  
  // If booking data is not in state, fetch it (in a real app)
  useEffect(() => {
    if (!booking && bookingId) {
      setLoading(true);
      
      // In a real app, this would be an API call to get booking details
      // For demo purposes, we'll create mock data
      
      // Find a random hotel if none is provided
      const mockHotel = hotel || hotelData.hotels[Math.floor(Math.random() * hotelData.hotels.length)];
      
      if (mockHotel) {
        setHotel(mockHotel);
        
        // Create mock room data
        const mockRoom = {
          id: 1,
          name: 'Standard Room',
          price: mockHotel.price,
          capacity: 2,
          bedType: 'Queen'
        };
        setRoom(mockRoom);
        
        // Create mock booking data
        const checkIn = new Date();
        checkIn.setDate(checkIn.getDate() + 30); // 30 days from now
        
        const checkOut = new Date(checkIn);
        checkOut.setDate(checkOut.getDate() + 3); // 3 nights stay
        
        const mockBooking = {
          id: bookingId,
          checkIn: checkIn.toISOString(),
          checkOut: checkOut.toISOString(),
          guests: { adults: 2, children: 0, rooms: 1 },
          guestName: 'John Doe',
          price: {
            basePrice: mockHotel.price * 3,
            taxesAndFees: Math.round(mockHotel.price * 3 * 0.15),
            total: Math.round(mockHotel.price * 3 * 1.15)
          },
          bookingDate: new Date().toISOString(),
          status: 'confirmed'
        };
        setBooking(mockBooking);
      }
      
      setLoading(false);
    }
  }, [bookingId, booking, hotel]);
  
  // Format date for display
  const formatDate = (date) => {
    if (!date) return '';
    if (typeof date === 'string') {
      date = new Date(date);
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  
  // Calculate total nights
  const calculateNights = () => {
    if (!booking?.checkIn || !booking?.checkOut) return 1;
    
    let checkIn = booking.checkIn;
    let checkOut = booking.checkOut;
    
    if (typeof checkIn === 'string') checkIn = new Date(checkIn);
    if (typeof checkOut === 'string') checkOut = new Date(checkOut);
    
    const diffTime = Math.abs(checkOut - checkIn);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays || 1;
  };
  
  // Handle print function
  const handlePrint = () => {
    window.print();
  };
  
  // Handle email function
  const handleEmail = () => {
    // In a real app, this would trigger an API call to send email
    alert('A copy of your booking confirmation has been sent to your email.');
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading booking confirmation...</p>
      </div>
    );
  }
  
  // Booking not found state
  if (!booking || !hotel) {
    return (
      <div className="container py-5 text-center">
        <h2>Booking Not Found</h2>
        <p className="text-muted">The booking you're looking for doesn't exist or has been removed.</p>
        <Link to="/" className="btn btn-primary mt-3">
          <Home size={18} className="me-2" />
          Back to Home
        </Link>
      </div>
    );
  }

  return (
    <div className="container py-4">
      {/* Success Message */}
      <div className="alert alert-success d-flex align-items-center mb-4" role="alert">
        <Check size={24} className="me-3" />
        <div>
          <h4 className="alert-heading mb-1">Booking Confirmed!</h4>
          <p className="mb-0">Your booking has been successfully completed. A confirmation email has been sent to your email address.</p>
        </div>
      </div>
      
      {/* Booking Reference */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row">
            <div className="col-md-8">
              <h1 className="h3 mb-2">Booking Confirmation</h1>
              <div className="d-flex flex-wrap gap-3 mb-3">
                <div>
                  <span className="text-muted">Booking ID:</span>
                  <span className="ms-1 fw-medium">{booking.id}</span>
                </div>
                <div>
                  <span className="text-muted">Booked on:</span>
                  <span className="ms-1">{formatDate(booking.bookingDate)}</span>
                </div>
                <div>
                  <span className="text-muted">Status:</span>
                  <span className="ms-1 badge bg-success">{booking.status}</span>
                </div>
              </div>
            </div>
            <div className="col-md-4 d-flex justify-content-md-end align-items-center mt-3 mt-md-0">
              <div className="btn-group">
                <button className="btn btn-outline-secondary" onClick={handlePrint}>
                  <Printer size={18} className="me-1" />
                  Print
                </button>
                <button className="btn btn-outline-secondary" onClick={handleEmail}>
                  <Mail size={18} className="me-1" />
                  Email
                </button>
                <button className="btn btn-outline-secondary">
                  <Download size={18} className="me-1" />
                  PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Booking Info */}
      <div className="row">
        <div className="col-lg-8">
          {/* Hotel and Room Info */}
          <div className="card mb-4">
            <div className="card-header bg-white">
              <h3 className="h5 mb-0">Stay Information</h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4 mb-3 mb-md-0">
                  <img 
                    src={hotel.image}
                    alt={hotel.name}
                    className="img-fluid rounded mb-2"
                    style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                    onError={(e) => {
                      e.target.src = '';
                    }}
                  />
                </div>
                <div className="col-md-8">
                  <h4 className="h5 mb-1">{hotel.name}</h4>
                  <div className="d-flex align-items-center mb-2">
                    <div className="text-warning d-flex me-2">
                      {[...Array(hotel.stars)].map((_, i) => (
                        <Star key={i} size={16} fill="currentColor" />
                      ))}
                    </div>
                    {hotel.halal_status && (
                      <span className="badge bg-success">{hotel.halal_status}</span>
                    )}
                  </div>
                  <div className="d-flex align-items-center text-muted mb-3">
                    <MapPin size={16} className="me-1" />
                    <small>{`${hotel.location.city}, ${hotel.location.country}`}</small>
                  </div>
                  
                  <h5 className="h6 mb-2">Room: {room.name}</h5>
                  <div className="text-muted mb-1 small">{room.bedType} Bed · Max. {room.capacity} guests</div>
                </div>
              </div>
              
              <hr className="my-4" />
              
              <div className="row">
                <div className="col-md-6 mb-3 mb-md-0">
                  <div className="d-flex align-items-center mb-2">
                    <Calendar size={18} className="text-muted me-2" />
                    <div className="fw-medium">Check-in</div>
                  </div>
                  <div>{formatDate(booking.checkIn)}</div>
                  <small className="text-muted">After 3:00 PM</small>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-center mb-2">
                    <Calendar size={18} className="text-muted me-2" />
                    <div className="fw-medium">Check-out</div>
                  </div>
                  <div>{formatDate(booking.checkOut)}</div>
                  <small className="text-muted">Before 12:00 PM</small>
                </div>
              </div>
              
              <hr className="my-4" />
              
              <div className="row">
                <div className="col-12">
                  <div className="d-flex align-items-center mb-2">
                    <Users size={18} className="text-muted me-2" />
                    <div className="fw-medium">Guest Information</div>
                  </div>
                  <div className="mb-1">
                    <span className="fw-medium">Primary Guest:</span>
                    <span className="ms-1">{booking.guestName}</span>
                  </div>
                  <div>
                    <span className="fw-medium">Guests:</span>
                    <span className="ms-1">
                      {booking.guests.adults} adults, 
                      {booking.guests.children > 0 ? ` ${booking.guests.children} children, ` : ' '}
                      {booking.guests.rooms} room
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Important Information */}
          <div className="card mb-4">
            <div className="card-header bg-white">
              <h3 className="h5 mb-0">Important Information</h3>
            </div>
            <div className="card-body">
              <h4 className="h6 mb-2">Cancellation Policy</h4>
              <p className="mb-3">Free cancellation until 24 hours before check-in. Cancel before {formatDate(booking.checkIn)} for a full refund.</p>
              
              <h4 className="h6 mb-2">Hotel Policies</h4>
              <ul className="mb-0">
                <li>Check-in time: 3:00 PM</li>
                <li>Check-out time: 12:00 PM</li>
                <li>Pets not allowed</li>
                <li>Non-smoking rooms</li>
                <li>Government-issued photo ID required at check-in</li>
              </ul>
            </div>
          </div>
          
          {/* Need Help */}
          <div className="card mb-4 mb-lg-0">
            <div className="card-header bg-white">
              <h3 className="h5 mb-0">Need Help?</h3>
            </div>
            <div className="card-body">
              <p>If you have any questions or need to make changes to your booking, please contact us:</p>
              <div className="row">
                <div className="col-md-6 mb-3 mb-md-0">
                  <h4 className="h6 mb-2">Customer Service</h4>
                  <p className="mb-1">Phone: +1 (555) 123-4567</p>
                  <p className="mb-0">Email: support@hotelsbooking.com</p>
                </div>
                <div className="col-md-6">
                  <h4 className="h6 mb-2">Hotel Contact</h4>
                  <p className="mb-1">Phone: {hotel.phone || '+1 (555) 987-6543'}</p>
                  <p className="mb-0">Email: {hotel.email || 'info@' + hotel.name.toLowerCase().replace(/\s+/g, '') + '.com'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Right Column: Payment Summary */}
        <div className="col-lg-4">
          <div className="card shadow-sm sticky-top" style={{ top: '20px', zIndex: 10 }}>
            <div className="card-header bg-white">
              <h3 className="h5 mb-0">Payment Summary</h3>
            </div>
            <div className="card-body">
              {/* Booking Details */}
              <div className="border-bottom pb-3 mb-3">
                <div className="d-flex justify-content-between mb-2">
                  <div className="text-muted">Booking ID</div>
                  <div>{booking.id}</div>
                </div>
                <div className="d-flex justify-content-between">
                  <div className="text-muted">Status</div>
                  <div><span className="badge bg-success">{booking.status}</span></div>
                </div>
              </div>
              
              {/* Stay Details */}
              <div className="border-bottom pb-3 mb-3">
                <div className="d-flex justify-content-between mb-2">
                  <div className="text-muted">Check-in</div>
                  <div>{formatDate(booking.checkIn)}</div>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <div className="text-muted">Check-out</div>
                  <div>{formatDate(booking.checkOut)}</div>
                </div>
                <div className="d-flex justify-content-between">
                  <div className="text-muted">Duration</div>
                  <div>{calculateNights()} {calculateNights() === 1 ? 'night' : 'nights'}</div>
                </div>
              </div>
              
              {/* Charges */}
              <div className="border-bottom pb-3 mb-3">
                <div className="d-flex justify-content-between mb-2">
                  <div>{room.name}</div>
                  <div>${(booking.price.basePrice / calculateNights()).toFixed(2)} × {calculateNights()}</div>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <div>Room Charge</div>
                  <div>${booking.price.basePrice.toFixed(2)}</div>
                </div>
                <div className="d-flex justify-content-between">
                  <div>Taxes and Fees</div>
                  <div>${booking.price.taxesAndFees.toFixed(2)}</div>
                </div>
              </div>
              
              {/* Total */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="fw-bold">Total Paid</div>
                <div className="h4 mb-0">${booking.price.total.toFixed(2)}</div>
              </div>
              
              {/* Payment Method */}
              <div className="mb-3">
                <div className="text-muted mb-1">Payment Method</div>
                <div className="fw-medium">Credit Card (ending in 1234)</div>
              </div>
              
              {/* Manage Booking Button */}
              <div className="d-grid gap-2">
                <button className="btn btn-outline-primary">
                  <FileText size={18} className="me-1" />
                  Manage Booking
                </button>
                <Link to="/" className="btn btn-primary">
                  <Home size={18} className="me-1" />
                  Book Another Stay
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HotelConfirmation;